/**
 * schwarzPPipeline — Schwarz P triply-periodic minimal surface.
 *
 * Schwarz P surface: cos(x) + cos(y) + cos(z) = 0. Another famous
 * triply-periodic minimal surface with a different topology from the
 * gyroid. Rendered with similar iridescent lighting.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SchwarzPPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

float sdSchwarzP(vec3 p) {
  float scale = 2.5 + bass * 0.5;
  vec3 q = p * scale;
  // Schwarz P surface: cos(x) + cos(y) + cos(z) = 0.
  float v = cos(q.x) + cos(q.y) + cos(q.z);
  // Gradient magnitude.
  float gradMag = length(vec3(
    -sin(q.x),
    -sin(q.y),
    -sin(q.z)
  ));
  return v / max(gradMag, 0.001) / scale;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    sdSchwarzP(p + e.xyy) - sdSchwarzP(p - e.xyy),
    sdSchwarzP(p + e.yxy) - sdSchwarzP(p - e.yxy),
    sdSchwarzP(p + e.yyx) - sdSchwarzP(p - e.yyx)
  ));
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  // Schwarz P 2D density projection.
  float t = uTime * 0.3;
  float angle = t;
  float ca = cos(angle), sa = sin(angle);
  mat2 rot = mat2(ca, -sa, sa, ca);
  vec2 rp = rot * p;
  vec3 col = vec3(0.04, 0.03, 0.08);
  float density = 0.0;
  for (int zi = 0; zi < 24; zi++) {
    float z = (float(zi) / 24.0 - 0.5) * 4.0 + t * 0.7;
    vec3 q = vec3(rp * 2.5, z * 2.5);
    // Schwarz P: cos(x) + cos(y) + cos(z) = 0.
    float v = cos(q.x) + cos(q.y) + cos(q.z);
    float d = exp(-abs(v) * 6.0) * (0.6 + bass * 0.4);
    vec3 layerColor = mix(
      vec3(0.30, 0.50, 0.95),
      vec3(0.95, 0.40, 0.85),
      float(zi) / 24.0
    );
    col += layerColor * d * 0.2;
    density += d;
  }
  col *= 1.0 + mid * 0.4;
  col = col / (1.0 + density * 0.1);
  col *= 1.0 + bass * 0.2;

  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeSchwarzPBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeSchwarzPRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSchwarzPPipeline(gl: WebGL2RenderingContext): SchwarzPPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSchwarzP(
  gl: WebGL2RenderingContext,
  pipe: SchwarzPPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
